<script setup lang="ts">
import TimeClockEnterIcon from '@/assets/icons/Employees/TimeClockEnterIcon.vue';
import TimeClockLeaveIcon from '@/assets/icons/Employees/TimeClockLeaveIcon.vue';
import ButtonWithIconWrapper from '@/components/BaseComponents/ButtonWithIconWrapper.vue';
import TimeEntryDeleteDialog from '@/components/dialogs/TimeEntryDeleteDialog.vue';
import TimeEntryDialog from '@/components/dialogs/TimeEntryDialog.vue';
import { useEmployee } from '@/composables/useEmployee';
import { useUserStore } from '@/stores/useUserStore';
import type { WorkingPeriod } from '@/types/employee';
import { formatRestaurantDate, formatRestaurantDateTime, formatTime, getCurrentRestaurantTime } from '@/utils/time';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Button from 'primevue/button';
import ConfirmDialog from 'primevue/confirmdialog';
import Menu from 'primevue/menu';
import { useToast } from 'primevue/usetoast';
import type { ComponentPublicInstance } from 'vue';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);

const { t } = useI18n();

onMounted(async () => {
  await initTimeClock();
  // Start interval to update ongoing period times
  intervalId.value = setInterval(updateCurrentTimes, 1000);
});

onUnmounted(() => {
  if (intervalId.value) {
    clearInterval(intervalId.value);
  }
});

const toast = useToast();
const router = useRouter();
const { startWorkingPeriod, endWorkingPeriod, getWorkingPeriods } = useEmployee();
const userStore = useUserStore();
const user = ref(userStore.user);

const workingPeriods = ref<WorkingPeriod[]>([]);
const loading = ref(true);
const intervalId = ref<number | ReturnType<typeof setInterval> | null>(null);

// Get restaurant timezone from user's active restaurant
const restaurantTimezone = computed(() => {
  return user.value?.active_restaurant?.timezone || 'UTC';
});

// Find any currently active period (ongoing or spanning current time)
const activePeriod = computed(() => {
  const now = getCurrentRestaurantTime(restaurantTimezone.value).utc();

  return (
    workingPeriods.value.find((period) => {
      // Check if backend provided the is_currently_active flag
      if (period.is_currently_active !== undefined) {
        return period.is_currently_active;
      }

      // Fallback check for periods without the new field
      if (period.end_time === null) {
        return dayjs.utc(period.start_time).isBefore(now);
      }

      // Check if current time is within the period
      const start = dayjs.utc(period.start_time);
      const end = dayjs.utc(period.end_time);

      return start.isBefore(now) && end.isAfter(now);
    }) || null
  );
});

// Find the ongoing period (without end_time) regardless of when it started
const ongoingPeriod = computed(() => {
  return workingPeriods.value.find((period) => period.end_time === null) || null;
});

// Check if there's an active period that's manually created (has both start and end time)
const hasManualActivePeriod = computed(() => {
  return activePeriod.value !== null && activePeriod.value?.end_time !== null;
});

// Check if there's an ongoing period (no end_time)
const hasOngoingPeriod = computed(() => {
  return ongoingPeriod.value !== null;
});

const currentStartTime = computed(() => {
  if (hasOngoingPeriod.value && ongoingPeriod.value) {
    return ongoingPeriod.value.start_time;
  }
  if (activePeriod.value) {
    return activePeriod.value.start_time;
  }
  return null;
});

// Check if the active period is very old (more than 24 hours)
const isLongRunningPeriod = computed(() => {
  if (!activePeriod.value) return false;

  const start = dayjs.utc(activePeriod.value.start_time);
  const now = getCurrentRestaurantTime(restaurantTimezone.value).utc();

  return now.diff(start, 'hour') > 24;
});

// Total seconds worked for the day (in restaurant timezone)
const totalSecondsWorked = computed(() => {
  const today = formatRestaurantDate(undefined, restaurantTimezone.value);

  return workingPeriods.value
    .filter((period) => {
      // Only include periods that started today
      const periodDate = formatRestaurantDate(period.start_time, restaurantTimezone.value);
      return periodDate === today;
    })
    .reduce((sum, period) => {
      const start = dayjs.utc(period.start_time);
      const end = period.end_time
        ? dayjs.utc(period.end_time)
        : hasOngoingPeriod.value && period === ongoingPeriod.value
          ? getCurrentRestaurantTime(restaurantTimezone.value).utc()
          : null;

      if (end) {
        return sum + end.diff(start, 'second');
      }
      return sum;
    }, 0);
});

// Current time for updating ongoing periods
const currentTime = ref(getCurrentRestaurantTime(restaurantTimezone.value));

function updateCurrentTimes() {
  currentTime.value = getCurrentRestaurantTime(restaurantTimezone.value);
}

function formatPeriodDuration(start: string, end: string | null): string {
  const startTime = dayjs.utc(start);
  const endTime = end ? dayjs.utc(end) : currentTime.value.utc();
  const duration = dayjs.duration(endTime.diff(startTime));
  const hours = duration.hours();
  const minutes = duration.minutes();
  const days = duration.days();

  if (days > 0) {
    return t('common.dayShort.dayLabelNb', { count: days }) + ` ${hours}h${minutes.toString().padStart(2, '0')}`;
  }
  if (hours > 0) {
    return `${hours}h${minutes.toString().padStart(2, '0')}`;
  }
  return t('common.minutes', { count: minutes });
}

async function fetchAllWorkingPeriods() {
  if (!user.value || !user.value.active_restaurant) return;

  try {
    // Fetch only today's working periods; the backend always appends the ongoing
    // period (no end_time) regardless of date filters, covering the case where
    // an employee forgot to stop the clock.
    const today = formatRestaurantDate(undefined, restaurantTimezone.value);
    const { data, error } = await getWorkingPeriods(user.value.active_restaurant.restaurant_user_id, {
      start_date: today,
      end_date: today,
    });

    if (error) {
      console.error(error);
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Une erreur est survenue lors de la récupération des périodes de travail',
        life: 3000,
      });
      return;
    }

    if (data && data.working_periods) {
      workingPeriods.value = data.working_periods as WorkingPeriod[];
    } else {
      workingPeriods.value = [];
    }
  } catch (err) {
    console.error('Error fetching working periods:', err);
    workingPeriods.value = [];
  }
}

async function initTimeClock() {
  loading.value = true;

  // Check if user needs to be refreshed
  if (!user.value) {
    await userStore.refreshUser();
    user.value = userStore.user;
  }

  await fetchAllWorkingPeriods();
  loading.value = false;
}

const handleStart = async () => {
  loading.value = true;
  if (!user.value || !user.value.active_restaurant) return;

  try {
    const result = await startWorkingPeriod(user.value.active_restaurant.restaurant_user_id);

    if (result.error) {
      // Handle the case where there's already an open period
      if (result.error.includes('open_period_exists')) {
        toast.add({
          severity: 'info',
          summary: 'Information',
          detail: 'Une période de travail est déjà ouverte',
          life: 3000,
        });
      } else {
        toast.add({
          severity: 'error',
          summary: 'Erreur',
          detail: result.error,
          life: 3000,
        });
      }
    }

    // Refresh data regardless of result to show current state
    await fetchAllWorkingPeriods();
  } catch (error) {
    console.error('Error starting working period:', error);
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: 'Impossible de démarrer la période de travail',
      life: 3000,
    });
  }

  loading.value = false;
};

const handleStop = async () => {
  loading.value = true;
  if (!user.value || !user.value.active_restaurant) return;

  try {
    const result = await endWorkingPeriod(user.value.active_restaurant.restaurant_user_id);

    if (result.error) {
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: result.error,
        life: 3000,
      });
    }

    // Always refresh data after attempting to end period
    await fetchAllWorkingPeriods();
  } catch (error) {
    console.error('Error ending working period:', error);
    toast.add({
      severity: 'error',
      summary: 'Erreur',
      detail: "Impossible d'arrêter la période de travail",
      life: 3000,
    });
  }

  loading.value = false;
};

// Get today's periods for display
const todaysPeriods = computed(() => {
  const today = formatRestaurantDate(undefined, restaurantTimezone.value);

  return workingPeriods.value.filter((period) => {
    const periodDate = formatRestaurantDate(period.start_time, restaurantTimezone.value);
    return periodDate === today;
  });
});

const currentDayLabel = computed(() => {
  const restaurantDate = getCurrentRestaurantTime(restaurantTimezone.value);
  const dayShort = restaurantDate.format('ddd');
  const dayLong = t(`common.dayShortToLong.${dayShort}`);
  const day = restaurantDate.format('DD');
  const month = restaurantDate.format('MM');
  const year = restaurantDate.format('YYYY');
  return `${dayLong} ${day}.${month}.${year}`;
});

// Check if current user can edit working periods
const canEditWorkingPeriods = computed(() => {
  const activeRestaurant = user.value?.active_restaurant;
  if (!activeRestaurant) return false;

  // Admins and managers can always edit
  if (activeRestaurant.role === 'admin' || activeRestaurant.role === 'manager') {
    return true;
  }

  // For other roles, check if the restaurant allows employee edits
  return activeRestaurant.allow_employee_working_period_edits === true;
});

// Dialog states
const showEditDialog = ref(false);
const showDeleteDialog = ref(false);
const selectedPeriod = ref<WorkingPeriod | null>(null);

// Menu references
const periodMenus = ref<{ [key: number]: ComponentPublicInstance & { toggle: (event: Event) => void } }>({});

// Restaurant user ID for API calls
const restaurantUserId = computed(() => {
  return user.value?.active_restaurant?.restaurant_user_id ?? 0;
});

const openEditDialog = (period: WorkingPeriod) => {
  selectedPeriod.value = period;
  showEditDialog.value = true;
};

const openDeleteDialog = (period: WorkingPeriod) => {
  selectedPeriod.value = period;
  showDeleteDialog.value = true;
};

const handlePeriodSaved = (_savedPeriod: WorkingPeriod) => {
  fetchAllWorkingPeriods();
};

const handlePeriodDeleted = (_periodId: number) => {
  fetchAllWorkingPeriods();
};

const getPeriodMenuItems = (period: WorkingPeriod) => [
  {
    label: t('common.edit'),
    icon: 'pi pi-pencil',
    command: () => openEditDialog(period),
  },
  {
    label: t('common.delete'),
    icon: 'pi pi-trash',
    command: () => openDeleteDialog(period),
    class: 'text-red-500',
  },
];

const togglePeriodMenu = (event: Event, period: WorkingPeriod) => {
  periodMenus.value[period.id]?.toggle(event);
};
</script>

<template>
  <main class="relative flex h-full flex-col">
    <ConfirmDialog group="endDay">
      <template #container="{ message, acceptCallback, rejectCallback }">
        <div class="flex-column surface-overlay border-round flex p-5">
          <span class="block font-bold text-2xl">{{ message.header }}</span>
          <p class="mb-0">{{ message.message }}</p>
          <div class="align-items-center justify-content-end mt-4 flex gap-2">
            <Button label="Annuler" outlined @click="rejectCallback"></Button>
            <Button label="Confirmer" @click="acceptCallback"></Button>
          </div>
        </div>
      </template>
    </ConfirmDialog>
    <div id="home-header">
      <div class="relative">
        <div v-if="todaysPeriods.length == 0 && !activePeriod" class="text-welcome">
          <h1 v-if="user" class="m-auto my-2 w-4/5 text-center">
            <span class="hello-container max-w-full truncate">{{ t('homeView.hello') }} {{ user.first_name }} !</span>
          </h1>
          <h1 v-else class="my-2 text-center">
            <span class="hello-container">{{ t('homeView.hello') }} !</span>
          </h1>
        </div>
        <img class="absolute top-0 -z-10 max-h-[80vh] w-full rotate-180 opacity-10" src="@/assets/images/home-page-background.webp" />
      </div>
      <div class="avatar-image mt-4 text-center">
        <img src="@/assets/images/avatar-rounded.webp" :alt="t('homeView.altImageAvatar')" class="mx-auto max-h-[80vh] w-1/4 max-w-32" />
      </div>
    </div>
    <div class="mx-8 mt-8 flex flex-col items-center gap-4">
      <div>
        <h3>{{ currentDayLabel }}</h3>
      </div>

      <!-- Warning for long-running periods -->
      <div v-if="isLongRunningPeriod" class="w-full max-w-md rounded-lg border border-orange-300 bg-orange-100 p-3 text-orange-800">
        <div class="flex items-center gap-2">
          <i class="pi pi-exclamation-triangle"></i>
          <span class="text-sm font-medium">{{ t('employee.timeclock.longRunningPeriodWarning') }}</span>
        </div>
      </div>

      <!-- Warning for manually scheduled periods -->
      <div v-if="hasManualActivePeriod" class="w-full max-w-md rounded-lg border border-blue-300 bg-blue-100 p-3 text-blue-800">
        <div class="flex items-center gap-2">
          <i class="pi pi-calendar"></i>
          <span class="text-sm font-medium">{{ t('employee.timeclock.scheduledPeriodActive') }}</span>
        </div>
      </div>

      <div>
        <div class="text-gray-700">
          <span v-if="currentStartTime">{{ t('employee.timeclock.lastEntry') }}</span>
          <span v-else>{{ t('employee.timeclock.totalWorkedToday') }}</span>
        </div>
        <div class="text-center font-bold text-3xl">
          <span v-if="currentStartTime">
            {{ formatRestaurantDateTime(currentStartTime, 'HH:mm', restaurantTimezone) }}
          </span>
          <span v-else>
            {{ formatTime(totalSecondsWorked) }}
          </span>
        </div>
      </div>

      <div v-if="todaysPeriods.length > 0 || activePeriod" class="mt-4 w-full max-w-md">
        <div class="mb-2 font-semibold">
          <h3 class="text-sm">{{ t('employee.timeclock.entriesOfTheDay') }}</h3>
        </div>
        <ul>
          <!-- Show active period first if it exists and didn't start today -->
          <li v-if="activePeriod && !todaysPeriods.includes(activePeriod)" class="mb-1 rounded border border-yellow-200 bg-yellow-50 p-2">
            <div class="mb-1 text-xs text-yellow-700">{{ t('employee.timeclock.ongoingFromPreviousDay') }}</div>
            <div class="flex items-center justify-between">
              <span>
                {{ formatRestaurantDateTime(activePeriod.start_time, 'DD/MM HH:mm', restaurantTimezone) }}
                -
                <span v-if="activePeriod.end_time">{{ formatRestaurantDateTime(activePeriod.end_time, 'DD/MM HH:mm', restaurantTimezone) }}</span>
                <span v-else class="font-semibold text-green-600">{{ t('employee.timeclock.onGoing') }}</span>
                <span class="ml-2 text-gray-500"> ({{ formatPeriodDuration(activePeriod.start_time, activePeriod.end_time) }}) </span>
              </span>
              <div v-if="canEditWorkingPeriods && activePeriod" class="flex items-center">
                <Button icon="pi pi-ellipsis-v" text size="small" :aria-label="t('common.more')" @click="togglePeriodMenu($event, activePeriod!)" />
                <Menu
                  :ref="
                    (el) => {
                      if (el && typeof el === 'object' && 'toggle' in el) periodMenus[activePeriod!.id] = el as any;
                    }
                  "
                  :model="getPeriodMenuItems(activePeriod!)"
                  popup
                />
              </div>
            </div>
          </li>

          <!-- Show today's periods -->
          <li v-for="period in todaysPeriods" :key="period.id" class="mb-1 flex items-center justify-between">
            <span>
              {{ formatRestaurantDateTime(period.start_time, 'HH:mm', restaurantTimezone) }}
              -
              <span v-if="period.end_time">{{ formatRestaurantDateTime(period.end_time, 'HH:mm', restaurantTimezone) }}</span>
              <span v-else-if="period.is_currently_active || period === activePeriod" class="font-semibold text-green-600">{{
                t('employee.timeclock.onGoing')
              }}</span>
              <span v-else>{{ period.end_time ? formatRestaurantDateTime(period.end_time, 'HH:mm', restaurantTimezone) : '' }}</span>
              <span class="ml-2 text-gray-500"> ({{ formatPeriodDuration(period.start_time, period.end_time) }}) </span>
            </span>
            <div v-if="canEditWorkingPeriods" class="flex items-center">
              <Button icon="pi pi-ellipsis-v" text size="small" :aria-label="t('common.more')" @click="togglePeriodMenu($event, period)" />
              <Menu
                :ref="
                  (el) => {
                    if (el && typeof el === 'object' && 'toggle' in el) periodMenus[period.id] = el as any;
                  }
                "
                :model="getPeriodMenuItems(period)"
                popup
              />
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div
      class="sticky bottom-16 left-0 z-50 mt-8 flex items-center justify-center gap-3 border-t border-gray-200 bg-white px-8 py-2">

      <!-- Shortcut to all time entries -->
      <Button v-if="canEditWorkingPeriods" icon="pi pi-history" outlined
        :aria-label="t('employee.timeclock.timeEntries')" :title="t('employee.timeclock.timeEntries')"
        @click="router.push(`/employee/${restaurantUserId}/time-entries`)" />

      <!-- Check In Button: Show when no active period -->
      <Button v-if="!hasOngoingPeriod && !hasManualActivePeriod" class="col-span-1 w-full max-w-64" :disabled="loading" @click="handleStart">
        <ButtonWithIconWrapper>
          <template #icon>
            <TimeClockEnterIcon />
          </template>
          <template #label>
            {{ t('employee.timeclock.checkIn') }}
          </template>
        </ButtonWithIconWrapper>
      </Button>

      <!-- Check Out Button: Show only when there's an ongoing period (no end_time) -->
      <Button v-else-if="hasOngoingPeriod" class="w-full max-w-64" :disabled="loading" severity="danger" @click="handleStop">
        <ButtonWithIconWrapper>
          <template #icon>
            <TimeClockLeaveIcon />
          </template>
          <template #label>
            {{ t('employee.timeclock.checkOut') }}
          </template>
        </ButtonWithIconWrapper>
      </Button>

      <!-- Disabled Button: Show when there's a manually scheduled active period -->
      <Button v-else-if="hasManualActivePeriod" class="w-full max-w-64" :disabled="true" severity="secondary">
        <ButtonWithIconWrapper>
          <template #icon>
            <i class="pi pi-calendar"></i>
          </template>
          <template #label>
            {{ t('employee.timeclock.scheduledPeriod') }}
          </template>
        </ButtonWithIconWrapper>
      </Button>
    </div>

    <!-- Edit / Delete Dialogs -->
    <TimeEntryDialog
      v-if="canEditWorkingPeriods"
      v-model:visible="showEditDialog"
      :restaurant-user-id="restaurantUserId"
      :working-period="selectedPeriod"
      mode="edit"
      @saved="handlePeriodSaved"
    />

    <TimeEntryDeleteDialog
      v-if="canEditWorkingPeriods"
      v-model:visible="showDeleteDialog"
      :restaurant-user-id="restaurantUserId"
      :working-period="selectedPeriod"
      @deleted="handlePeriodDeleted"
    />
  </main>
</template>
